import Text from "@/components/Text";
import Theme from "@/utils/theme";
import { useMediaChallengeSubmission } from "./useMediaChallengeSubmission";
import ShapedAccordion from "@/components/ShapedAccordion";
import { Box, CircularProgress, Stack } from "@mui/material";
import type { ChallengeTask } from "@/services/challenges/types/challenges.types";
import Spacer from "@/components/Spacer";
import Button from "@/components/Button";
import { DashedContainer, TaskImage, TaskVideo } from "./styles";
import IconButton from "@/components/IconButton";
import {
  MediaType,
  TaskChallengeStatus,
} from "@/services/challenges/types/challenges.enums";
import Dialog from "@/components/Dialog";
import ChallengeSubmissionWrapper from "@/layout/ChallengeSubmissionWrapper";
import { TaskVideoInput } from "../TaskVideoInput";
import { TaskImageInput } from "../TaskImageInput";
import MediaDetailsDialog from "../MediaDetailsDialog";
import { useStages } from "@/context/StagesContext";
import DeleteIcon from "@/ui/icons/DeleteIcon";
import InfoIcon from "@/ui/icons/InfoIcon";
import Chip from "@/components/Chip";
import HighlightedText from "@/components/HighlightedText";
import { format } from "date-fns";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChallengeTooltip } from "../ChallengeTooltip";

function MediaChallengeSubmission() {
  const { isValidStage, currentStage } = useStages();
  const {
    challenge,
    getTaskStatus,
    defaultOpenIndex,

    openMediaDetailsId,
    toggleMediaDetails,

    showHasChangesDialog,
    toggleHasChangesDialog,

    accordionKey,
    accordionIndexToClose,
    taskMedias,
    setTaskMedia,
    removeTaskMedia,
    getMediaStatus,
    taskInputs,
    hideTooltip,
    getShowTooltip,
    getDetailsButton,

    showRequiredInputDialog,
    toggleRequiredInputDialog,
    deleteTaskId,
    toggleDeleteTaskDialog,

    updateTaskInputValue,
    onSubmitTask,
    loadingTaskId,

    onClickBack,
    navigateToChallenges,
    onClickUploadArea,
    showHasDeleteDialog,
    toggleHasDeleteDialog,

    requireAllMediaBeforeSubmit,
    allTasksHaveMediaSelected,

    sortedTasks,
    allTasksCustomInputsValid,
    onSubmitAllPhotoTasks,
    loadingBatchSubmit,
    hasPendingTasksToSubmit,
  } = useMediaChallengeSubmission();

  const renderTaskMediaBlock = (
    task: ChallengeTask,
    index: number,
    opts: { showSubmitButton: boolean; photoSlotLabel?: string }
  ) => {
    const mediaUrl = taskMedias[task.id];
    const mediaStatus = getMediaStatus(task);
    const isEditable =
      (task.status == TaskChallengeStatus.PENDING ||
        task.status == TaskChallengeStatus.REJECTED) &&
      isValidStage &&
      currentStage?.id === challenge?.stageId;

    const detailsButton = getDetailsButton(task);
    const isPhotoGrid = opts.photoSlotLabel !== undefined;
    const showFileInputTooltip = isPhotoGrid
      ? index === 0 && getShowTooltip(task.id, "file-input")
      : getShowTooltip(task.id, "file-input");

    return (
      <Box
        display="flex"
        flexDirection="column"
        p={isPhotoGrid ? 0 : "1.5rem"}
        flex={1}
      >
        {opts.photoSlotLabel ? (
          <Text
            fontWeight={600}
            fontSize="0.875rem"
            color={Theme.colors.text.primary}
            mb="0.125rem"
          >
            {opts.photoSlotLabel}
          </Text>
        ) : null}

        {task.status === TaskChallengeStatus.REJECTED ? (
          <Stack
            py={1}
            px={2}
            flexDirection="row"
            alignItems="center"
            border={`1px solid ${Theme.colors.feedback.negative}`}
            borderRadius="0.5rem"
            mt={isPhotoGrid ? 0 : "1rem"}
          >
            <InfoOutlinedIcon
              sx={{
                color: Theme.colors.feedback.negative,
                width: "2rem",
                height: "2rem",
              }}
            />

            <Spacer width="1rem" />
            {currentStage?.endDate && (
              <HighlightedText
                color={Theme.colors.text.tertiary}
                text={`A foto enviada foi: {{Reprovada}} | Visualize o motivo da reprovação clicando no ícone informativo. Em seguida, exclua a imagem reprovada e faça um novo envio. Envio até: {{${format(
                  currentStage?.endDate,
                  "dd/MM/yyy"
                )}}}.`}
                highlightColor={Theme.colors.feedback.negative}
                hightlightWeight={900}
              />
            )}
          </Stack>
        ) : null}

        <Box
          display="flex"
          flexDirection={
            task.status === TaskChallengeStatus.REJECTED
              ? "column-reverse"
              : "column"
          }
        >
          {isEditable && !mediaUrl && (
            <Box
              mt={isPhotoGrid ? "0.25rem" : "1rem"}
              onClick={onClickUploadArea(task.status)}
            >
              {task.mediaType === MediaType.VIDEO ? (
                <TaskVideoInput
                  disabled={
                    task.status !== TaskChallengeStatus.PENDING || !!mediaUrl
                  }
                  setVideoUrl={(url) => setTaskMedia(task, url)}
                  taskId={task.id}
                  maxSeconds={task.maxSeconds}
                  showTooltip={showFileInputTooltip}
                  onCloseTooltip={() => hideTooltip(task.id, "file-input")}
                />
              ) : (
                <TaskImageInput
                  disabled={
                    task.status !== TaskChallengeStatus.PENDING || !!mediaUrl
                  }
                  setImageUrl={(url) => setTaskMedia(task, url)}
                  taskId={task.id}
                  legend={isPhotoGrid ? undefined : opts.photoSlotLabel}
                  showTooltip={showFileInputTooltip}
                  onCloseTooltip={() => hideTooltip(task.id, "file-input")}
                />
              )}
            </Box>
          )}

          {isPhotoGrid ? (
            <DashedContainer
              mt="0.25rem"
              display={!mediaUrl ? "none" : "flex"}
              flexDirection="column"
              alignItems="stretch"
              justifyContent="flex-start"
              gap={1}
              minWidth={0}
            >
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                sx={{ minWidth: 0 }}
              >
                {task.mediaType === MediaType.VIDEO ? (
                  <TaskVideo
                    src={mediaUrl}
                    style={{
                      width: "100%",
                      maxWidth: 160,
                      height: "auto",
                      aspectRatio: "1/1",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <TaskImage
                    src={mediaUrl}
                    style={{
                      width: "100%",
                      maxWidth: 160,
                      height: "auto",
                      aspectRatio: "1/1",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                gap={0.25}
                width="100%"
                minWidth={0}
              >
                <Text
                  fontSize="0.75rem"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    task.mediaType === MediaType.VIDEO
                      ? "video.mp4"
                      : "image.jpg"
                  }
                >
                  {task.mediaType === MediaType.VIDEO
                    ? "video.mp4"
                    : "image.jpg"}
                </Text>
                <Text
                  fontSize="0.8125rem"
                  fontWeight={500}
                  color={mediaStatus.color}
                >
                  {mediaStatus.text}
                </Text>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                gap={1}
                justifyContent="center"
                alignItems="center"
                flexWrap="wrap"
              >
                <Box position="relative">
                  <ChallengeTooltip
                    isOpen={getShowTooltip(task.id, "details-button")}
                    onClose={() => hideTooltip(task.id, "details-button")}
                    placement="top"
                    title="Agora é a sua vez de nos contar sobre a foto escolhida. Clique no botão para abrir o campo descritivo, salve a descrição e depois clique em “Enviar” para enviar a foto e o comentário."
                  />

                  <IconButton
                    icon={detailsButton.icon}
                    color={detailsButton.color}
                    onClick={() => toggleMediaDetails(task.id)}
                  />
                </Box>

                <IconButton
                  disabled={!isEditable}
                  icon="delete"
                  color={Theme.colors.feedback.negative}
                  onClick={() => toggleDeleteTaskDialog(task.id)}
                />
              </Box>
            </DashedContainer>
          ) : (
            <DashedContainer
              mt="1rem"
              display={!mediaUrl ? "none" : "flex"}
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent={{ xs: "flex-start", sm: "space-between" }}
              gap={{ xs: 1.25, sm: 0 }}
              minWidth={0}
            >
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems="center"
                gap={{ xs: 1, sm: 0 }}
                minWidth={0}
                flex={1}
              >
                {task.mediaType === MediaType.VIDEO ? (
                  <TaskVideo src={mediaUrl} />
                ) : (
                  <TaskImage src={mediaUrl} />
                )}

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  ml={{ xs: 0, sm: "1rem" }}
                  minWidth={0}
                  gap={{ xs: 0.25, sm: 0 }}
                >
                  <Text>
                    {task.mediaType === MediaType.VIDEO
                      ? "video.mp4"
                      : "image.jpg"}
                  </Text>
                  <Text
                    color={Theme.colors.neutral.mid}
                    display={{ xs: "none", sm: "block" }}
                    mx="0.5rem"
                  >
                    |
                  </Text>
                  <Text color={mediaStatus.color}>{mediaStatus.text}</Text>
                </Box>
              </Box>

              <Box display="flex" flexShrink={0}>
                <Box position="relative">
                  <ChallengeTooltip
                    isOpen={getShowTooltip(task.id, "details-button")}
                    onClose={() => hideTooltip(task.id, "details-button")}
                    placement="top"
                    title="Agora é a sua vez de nos contar sobre a foto escolhida. Clique no botão para abrir o campo descritivo, salve a descrição e depois clique em “Enviar” para enviar a foto e o comentário."
                  />

                  <IconButton
                    icon={detailsButton.icon}
                    color={detailsButton.color}
                    onClick={() => toggleMediaDetails(task.id)}
                  />
                </Box>

                <Spacer width="0.5rem" />

                <IconButton
                  disabled={!isEditable}
                  icon="delete"
                  color={Theme.colors.feedback.negative}
                  onClick={() => toggleDeleteTaskDialog(task.id)}
                />
              </Box>
            </DashedContainer>
          )}
        </Box>

        {opts.showSubmitButton ? (
          <Box display="flex" justifyContent="end" mt="1rem">
            <Button
              fullWidth={false}
              disabled={
                !mediaUrl ||
                task.status !== TaskChallengeStatus.PENDING ||
                (requireAllMediaBeforeSubmit && !allTasksHaveMediaSelected)
              }
              onClick={() => {
                onSubmitTask(task.id, mediaUrl);
              }}
              style={{ padding: "0.2rem 2rem" }}
            >
              {loadingTaskId === task.id ? (
                <CircularProgress
                  style={{ color: "white" }}
                  size="1.5rem"
                />
              ) : (
                "Enviar"
              )}
            </Button>
          </Box>
        ) : null}

        <MediaDetailsDialog
          isOpen={openMediaDetailsId === task.id}
          variant={task.mediaType === MediaType.VIDEO ? "video" : "photo"}
          mediaUrl={mediaUrl}
          onClose={() => toggleMediaDetails(task.id)}
          customInputs={task.customInputs ?? []}
          taskInputs={taskInputs[task.id] ?? {}}
          disableCustomInputs={task.status !== TaskChallengeStatus.PENDING}
          setTaskInput={updateTaskInputValue(task.id)}
          feedback={task.feedback}
        />

        <Dialog
          icon={<DeleteIcon />}
          iconSize={48}
          isOpen={deleteTaskId === task.id}
          text={`Tem certeza que deseja remover ${
            task.mediaType === MediaType.VIDEO
              ? "o vídeo inserido"
              : "a foto inserida"
          }?`}
          confirmButtonText="Não Remover"
          onConfirm={() => toggleDeleteTaskDialog(task.id)}
          closeButtonText="Sim, Tenho Certeza"
          onClose={() => removeTaskMedia(task)}
        />
      </Box>
    );
  };

  const anchorTaskForGridSummary =
    requireAllMediaBeforeSubmit && sortedTasks.length
      ? sortedTasks.find(
          (t) =>
            t.status === TaskChallengeStatus.PENDING ||
            t.status === TaskChallengeStatus.REJECTED
        ) ?? sortedTasks[0]
      : undefined;

  const gridSummaryStatus = anchorTaskForGridSummary
    ? getTaskStatus(anchorTaskForGridSummary)
    : null;

  const gridShadowColor =
    requireAllMediaBeforeSubmit && sortedTasks.length
      ? sortedTasks.some((t) => t.status === TaskChallengeStatus.REJECTED)
        ? Theme.colors.feedback.negative
        : sortedTasks.every(
            (t) =>
              t.status === TaskChallengeStatus.APPROVED ||
              t.status === TaskChallengeStatus.FINISHED
          )
        ? Theme.colors.feedback.positive
        : Theme.colors.brand.primary
      : Theme.colors.brand.primary;

  return (
    <ChallengeSubmissionWrapper onClickBackOverwrite={onClickBack}>
      {requireAllMediaBeforeSubmit && sortedTasks.length && challenge ? (
        <ShapedAccordion
          defaultIndexesKey={accordionKey}
          fixedIndex={0}
          summaryStyle={{ backgroundColor: Theme.colors.brand.secondary }}
          items={[
            {
              defaultExpanded: true,
              summary: (
                <Box display="flex" alignItems="center">
                  <Text color={Theme.colors.text.primary}>
                    {challenge.title}
                  </Text>
                  <Text color={Theme.colors.text.primary}> | </Text>
                  {gridSummaryStatus ? (
                    <Chip
                      label={gridSummaryStatus.text}
                      color={gridSummaryStatus.color}
                    />
                  ) : null}
                </Box>
              ),
              details: (
                <Box px="1.5rem" pb="1.5rem" pt="1.25rem">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(5, minmax(0, 1fr))",
                      },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {sortedTasks.map((task, index) => (
                      <Box key={task.id} minWidth={0}>
                        {renderTaskMediaBlock(task, index, {
                          showSubmitButton: false,
                          photoSlotLabel: `Foto ${index + 1}`,
                        })}
                      </Box>
                    ))}
                  </Box>

                  <Box display="flex" justifyContent="end" mt="1.5rem">
                    <Button
                      fullWidth={false}
                      disabled={
                        !hasPendingTasksToSubmit ||
                        !allTasksHaveMediaSelected ||
                        !allTasksCustomInputsValid ||
                        loadingBatchSubmit ||
                        !isValidStage ||
                        currentStage?.id !== challenge?.stageId
                      }
                      onClick={() => {
                        onSubmitAllPhotoTasks();
                      }}
                      style={{ padding: "0.2rem 2rem" }}
                    >
                      {loadingBatchSubmit ? (
                        <CircularProgress
                          style={{ color: "white" }}
                          size="1.5rem"
                        />
                      ) : (
                        "Enviar"
                      )}
                    </Button>
                  </Box>
                </Box>
              ),
              shadowColor: gridShadowColor,
            },
          ]}
        />
      ) : (
        <ShapedAccordion
          defaultIndexesKey={accordionKey}
          indexToClose={accordionIndexToClose}
          summaryStyle={{ backgroundColor: Theme.colors.brand.secondary }}
          items={sortedTasks.map((task, index) => {
            const status = getTaskStatus(task);

            const shadowColor =
              task.status == TaskChallengeStatus.REJECTED
                ? Theme.colors.feedback.negative
                : task.status == TaskChallengeStatus.APPROVED ||
                  task.status == TaskChallengeStatus.FINISHED
                ? Theme.colors.feedback.positive
                : Theme.colors.brand.primary;

            return {
              defaultExpanded: index === defaultOpenIndex,
              summary: (
                <Box display="flex" alignItems="center">
                  <Text color={Theme.colors.text.primary}>{task.title}</Text>
                  <Text color={Theme.colors.text.primary}> | </Text>
                  <Chip label={status.text} color={status.color} />
                </Box>
              ),
              details: renderTaskMediaBlock(task, index, {
                showSubmitButton: true,
              }),
              shadowColor,
            };
          })}
        />
      )}

      <Dialog
        isOpen={showRequiredInputDialog}
        text={`Atenção!\nÉ necessário que você deixe uma descrição sobre a imagem clicando no botão de detalhes.`}
        onConfirm={toggleRequiredInputDialog}
      />

      <Dialog
        isOpen={showHasChangesDialog}
        text={`Atenção!\nVocê está saindo dessa página com envio pendente. Caso concorde em sair, todas as alterações serão perdidas. Deseja realmente sair dessa página?`}
        confirmButtonText="Continuar nessa página"
        onConfirm={toggleHasChangesDialog}
        closeButtonText="Sair dessa página"
        onClose={navigateToChallenges}
      />

      <Dialog
        isOpen={showHasDeleteDialog}
        text={`Atenção\nÉ necessário que você exclua a imagem enviada anteriormente, antes de prosseguir com o envio da próxima.`}
        confirmButtonText="Entendi"
        onConfirm={toggleHasDeleteDialog}
        icon={<InfoIcon size={32} color={Theme.colors.feedback.negative} />}
      />
    </ChallengeSubmissionWrapper>
  );
}

export default MediaChallengeSubmission;
